/** @format */

import {
	APP_ID,
	ApplicationConfig,
	ErrorHandler,
	inject,
	provideAppInitializer
} from '@angular/core';
import {
	AddToCartEffects,
	FulfillmentButtonsEffects,
	addToCartReducers,
	fulfillmentButtonsReducer
} from '@pdp/add-to-cart/data-access';
import { AttributesEffects, attributesReducers } from '@pdp/attributes/data-access';
import {
	BVPixelEffects,
	ProductReviewsFilterEffects,
	ProductReviewsSummaryEffects,
	ReviewAiProsConsEffects,
	productReviewReducers
} from '@product-reviews/data-access';
import {
	BundlesEffects,
	HeaderLocationEffects,
	ProductSelectionEffects,
	WarrantiesEffects,
	appInitializerKillswitchesSet,
	bundlesReducer,
	killswitchesReducer,
	productSelectionReducers,
	warrantiesReducer
} from '@pdp/data-access/state';
import { Chain, Environment, LocalStorageConfig } from '@shared/data-access/interfaces';
import {
	CustomUrlSerializer,
	KillswitchService,
	LoggerConfig,
	Store,
	TaggingService,
	WINDOW_PROVIDERS
} from '@shared/data-access/services';
import { DCSG_APP_NAME, GlobalErrorHandler } from '@logger';
import { Store as NgrxStore, provideState, provideStore } from '@ngrx/store';
import { UrlSerializer, provideRouter } from '@angular/router';
import { commonReducer, headerLocationReducer, homrReducer } from '@state/data-access';
import { provideClientHydration, withNoHttpTransferCache } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';

import { APP_BASE_HREF } from '@angular/common';
import { AUTH_URL_FILTER } from '@shared/authentication/data-access';
import { HTTP_INTERCEPTOR_PROVIDERS } from './app.interceptors';
import { PRODUCT_APP_ROUTES } from './app.routes';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ProductSelectionState } from '@pdp/data-access/interfaces';
import { chain } from '@core/chain';
import { environment } from '../environments/environment';
import { productReviewReducers as productSocialContentReducers } from '@social-content/data-access';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { take } from 'rxjs';

const LOCAL_STORAGE_CONFIG: LocalStorageConfig = {
	prefix: ''
};
const LOGGER_CONFIG: LoggerConfig = {
	clientLoggerLevel: environment.logging.client.level,
	xhrLoggerLevel: environment.logging.xhr.level,
	xhrLoggerUrl: `${environment.api.spring.microServiceProxy.logger.path}publish`
};

export const appConfig: ApplicationConfig = {
	providers: [
		Store,
		provideStore({
			common: commonReducer,
			homr: homrReducer,
			headerLocation: headerLocationReducer,
			router: routerReducer
		}),
		provideEffects([
			AddToCartEffects,
			AttributesEffects,
			BundlesEffects,
			FulfillmentButtonsEffects,
			HeaderLocationEffects,
			ProductSelectionEffects,
			WarrantiesEffects,
			BVPixelEffects,
			ProductReviewsFilterEffects,
			ProductReviewsSummaryEffects,
			ReviewAiProsConsEffects
		]),
		provideState<ProductSelectionState>('productSelection', {
			addToCart: addToCartReducers,
			attributes: attributesReducers,
			bundles: bundlesReducer,
			killswitches: killswitchesReducer,
			productSelectionCore: productSelectionReducers,
			warranties: warrantiesReducer
		}),
		provideState('fulfillment', {
			fulfillmentButtonsReducer
		}),
		provideState('productReviews', productReviewReducers),
		provideState('productSocialContent', productSocialContentReducers),
		provideRouter(PRODUCT_APP_ROUTES),
		provideRouterStore(),
		provideStoreDevtools({
			maxAge: environment.production ? 5 : 25,
			logOnly: environment.production,
			connectInZone: true
		}),
		{ provide: LoggerConfig, useValue: LOGGER_CONFIG },
		provideHttpClient(withInterceptorsFromDi()),
		{ provide: Environment, useValue: environment },
		{ provide: APP_ID, useValue: 'dcsg-ngx-pdp-server' },
		{ provide: APP_BASE_HREF, useValue: '/' },
		{ provide: LocalStorageConfig, useValue: LOCAL_STORAGE_CONFIG },
		{
			provide: AUTH_URL_FILTER,
			useValue: [
				environment.baseUrls.pointService,
				environment.baseUrls.identityService,
				environment.baseUrls.athleteService,
				environment.api.myLocker
			]
		},
		/*
		 * APP_INITIALIZER InjectionToken is used to perform any logic during app initialization,
		 * this would execute before AppComponent or any Resolve Guards.  This guarantees anything
		 * required for setup is completed ahead of time.
		 */
		provideAppInitializer(() => {
			const initializerFn = (
				(service: ProductAnalyticsService) => () =>
					service.resetAnalytics()
			)(inject(ProductAnalyticsService));
			return initializerFn();
		}),
		provideAppInitializer(() => {
			const killswitchService = inject(KillswitchService);
			const store = inject(NgrxStore);
			killswitchService
				.getAllKillSwitchesForCurrentChain$()
				.pipe(take(1))
				.subscribe((killswitches) => {
					store.dispatch(appInitializerKillswitchesSet({ killswitches }));
				});
		}),
		WINDOW_PROVIDERS,
		{ provide: Chain, useValue: chain || {} },
		{ provide: DCSG_APP_NAME, useValue: 'PDP' },
		{ provide: ErrorHandler, useClass: GlobalErrorHandler },
		...HTTP_INTERCEPTOR_PROVIDERS,
		{ provide: UrlSerializer, useClass: CustomUrlSerializer },
		TaggingService,
		provideClientHydration(withNoHttpTransferCache()),
		provideAnimationsAsync()
	]
};
